// src/constants/blog.js
// Límites y constantes espejadas del backend del Blog/Feed (ver instrucciones/BLOG_FEED_FRONTEND.md).
// Se usan para validar en el cliente ANTES de subir y para mostrar mensajes coherentes con el server.

export const BLOG_LIMITS = {
  MAX_FILE_SIZE_BYTES: 150 * 1024 * 1024, // 150 MB por archivo
  MAX_MEDIA_FILES: 10, // hasta 10 archivos en media[]
  SUMMARY_MAX_LENGTH: 500,
  // Timeout de request generoso para cargas de video pesadas.
  REQUEST_TIMEOUT_MS: 300000, // 5 min
};

// Tipos MIME permitidos por el backend para media[] y cover.
export const ALLOWED_IMAGE_MIMES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'image/gif',
];

export const ALLOWED_VIDEO_MIMES = [
  'video/mp4',
  'video/webm',
  'video/quicktime', // .mov
];

export const ALLOWED_PDF_MIMES = ['application/pdf'];

export const ALLOWED_MEDIA_MIMES = [
  ...ALLOWED_PDF_MIMES,
  ...ALLOWED_IMAGE_MIMES,
  ...ALLOWED_VIDEO_MIMES,
];

// La portada solo admite imágenes.
export const ALLOWED_COVER_MIMES = ALLOWED_IMAGE_MIMES;

// PowerPoint no se acepta: se debe exportar a PDF.
export const BLOCKED_MIMES = [
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
];

export const ALLOWED_MEDIA_LABEL = 'PDF, imágenes (JPG/PNG/WEBP/GIF) o video (MP4/WEBM/MOV)';

// Categorías disponibles (valor + etiqueta legible).
// El backend expone las etiquetas vía GET /blog/filters; esta lista es el fallback.
export const BLOG_CATEGORIES = [
  { value: 'novedades', label: 'Novedades' },
  { value: 'mercado', label: 'Mercado' },
  { value: 'capacitacion', label: 'Capacitación' },
  { value: 'eventos', label: 'Eventos' },
  { value: 'institucional', label: 'Institucional' },
  { value: 'tutoriales', label: 'Tutoriales' },
  { value: 'otro', label: 'Otro' },
];

// Estados de publicación.
export const BLOG_STATUSES = [
  { value: 'borrador', label: 'Borrador' },
  { value: 'publicado', label: 'Publicado' },
  { value: 'archivado', label: 'Archivado' },
];

// Opciones de orden del feed.
export const BLOG_SORTS = [
  { value: 'recientes', label: 'Más recientes' },
  { value: 'populares', label: 'Más vistos' },
  { value: 'antiguos', label: 'Más antiguos' },
];

// Helper para mostrar tamaños en MB.
export const bytesToMB = (b) => (b / (1024 * 1024)).toFixed(1);

// Devuelve el tipo simple ('pdf' | 'image' | 'video') a partir del MIME.
export const mediaTypeFromMime = (mime = '') => {
  if (ALLOWED_PDF_MIMES.includes(mime)) return 'pdf';
  if (ALLOWED_IMAGE_MIMES.includes(mime)) return 'image';
  if (ALLOWED_VIDEO_MIMES.includes(mime)) return 'video';
  return 'otro';
};

// Etiqueta legible de una categoría (usa BLOG_CATEGORIES como fallback).
export const categoryLabel = (value, categories = BLOG_CATEGORIES) =>
  categories.find((c) => c.value === value)?.label || value || '';

// ============================================================
// Instructivo de carga (se muestra en el panel de ayuda del formulario).
// ============================================================
export const BLOG_UPLOAD_GUIDE = [
  {
    title: '1. Datos de texto',
    items: [
      'Título: OBLIGATORIO. Es lo primero que se ve en la card del feed.',
      'Resumen: opcional, hasta 500 caracteres. Es el texto corto de la card.',
      'Descripción: opcional, admite formato HTML. Es el cuerpo completo del post.',
    ],
  },
  {
    title: '2. Clasificación',
    items: [
      'Categoría: novedades, mercado, capacitación, eventos, institucional, tutoriales u otro.',
      'Estado: Borrador (no visible en el feed), Publicado (visible) o Archivado.',
      'Al publicar por primera vez, la fecha de publicación se fija automáticamente.',
      'Destacado: fija el post arriba del feed.',
      'Etiquetas (tags): palabras libres para filtrar (ej.: inversión, 2026).',
    ],
  },
  {
    title: '3. Portada',
    items: [
      'Portada (cover): 1 imagen que se usa como miniatura de la card.',
      'Solo imágenes: JPG, PNG, WEBP o GIF.',
    ],
  },
  {
    title: '4. Archivos multimedia',
    items: [
      'Hasta 10 archivos por publicación.',
      'Formatos: PDF, imágenes (JPG/PNG/WEBP/GIF) o video (MP4/WEBM/MOV).',
      'Máximo 150 MB por archivo.',
      'PowerPoint NO se acepta: exportá el .pptx a PDF antes de subirlo.',
      'Un post puede ser solo texto (sin archivos).',
      'Título por archivo: opcional; se muestra junto a cada media en el detalle.',
    ],
  },
  {
    title: '5. Al editar una publicación',
    items: [
      'Podés agregar archivos nuevos sin borrar los existentes.',
      'Quitar media: marcá los archivos a eliminar (se borran al guardar).',
      'Reemplazar/quitar portada: subí una nueva o marcá "Quitar portada".',
      'Si cambiás el título, el enlace (slug) se regenera automáticamente.',
    ],
  },
  {
    title: '6. Importante sobre la visualización',
    items: [
      'El contenido NO se descarga: solo se visualiza dentro del sitio.',
      'Los archivos llevan marca de agua y protección anti-descarga.',
      'No compartas enlaces directos a los archivos: no funcionan fuera del sitio.',
    ],
  },
];
